(function () {
  'use strict';

  const ACTIVE_KEY = 'noju_active_bogo_offer';
  const CLOSED_KEY = 'noju_closed_bogo_offer';
  const MINIMIZED_KEY = 'noju_minimized_bogo_offer';

  const ROOT_SELECTOR = '[data-bogo-notification]';
  const TITLE_SELECTOR = '[data-bogo-title]';
  const DESCRIPTION_SELECTOR = '[data-bogo-description]';
  const CTA_SELECTOR = '[data-bogo-cta]';

  // New minimize/reopen buttons.
  const MINIMIZE_SELECTOR = '[data-bogo-minimize]';
  const REOPEN_SELECTOR = '[data-bogo-reopen]';

  // Keep support for the old close button if it still exists.
  const CLOSE_SELECTOR = '[data-bogo-close]';

  let activeCampaign = null;
  let cartCheckInProgress = false;
  let lastCartCheck = 0;

  let dismissedCampaignOnCurrentPage = null;

  /**
   * Convert campaign names into comparable identifiers.
   * Example: "GRATIS MASCARA" -> "gratis-mascara"
   */
  function normalize(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  }

  /**
   * Safely read the minimized campaign ID.
   */
  function getMinimizedCampaignId() {
    try {
      return sessionStorage.getItem(MINIMIZED_KEY);
    } catch (error) {
      return null;
    }
  }

  /**
   * Remember that the campaign has been minimized.
   */
  function setMinimizedCampaignId(campaignId) {
    try {
      sessionStorage.setItem(
        MINIMIZED_KEY,
        String(campaignId)
      );
    } catch (error) {
      console.warn(
        '[BOGO Notification] Could not save minimized state.',
        error
      );
    }
  }

  /**
   * Clear the minimized state.
   */
  function clearMinimizedCampaignId() {
    try {
      sessionStorage.removeItem(MINIMIZED_KEY);
    } catch (error) {
      console.warn(
        '[BOGO Notification] Could not clear minimized state.',
        error
      );
    }
  }

  /**
   * Show the floating gift button.
   */
  function showReopenButton() {
    const button = document.querySelector(REOPEN_SELECTOR);

    if (button) {
      button.hidden = false;
      button.style.display = 'inline-flex';
      button.setAttribute('aria-hidden', 'false');
    }
  }

  /**
   * Hide the floating gift button.
   */
  function hideReopenButton() {
    const button = document.querySelector(REOPEN_SELECTOR);

    if (button) {
      button.hidden = true;
      button.style.display = 'none';
      button.setAttribute('aria-hidden', 'true');
    }
  }

  /**
   * Read campaigns directly from KITA.
   */
  function getCampaigns() {
    const gfg = window.gfg;

    if (!gfg || !gfg.settings) {
      return [];
    }

    let campaigns = [];

    if (
      gfg.utility &&
      typeof gfg.utility.getFreeGiftV2ModuleCampaigns === 'function'
    ) {
      try {
        campaigns =
          gfg.utility.getFreeGiftV2ModuleCampaigns(
            gfg.settings
          ) || [];
      } catch (error) {
        console.warn(
          '[BOGO Notification] Could not read KITA campaigns.',
          error
        );
      }
    }

    // Fallback if KITA's utility function is unavailable.
    if (!Array.isArray(campaigns) || campaigns.length === 0) {
      campaigns = [
        ...(gfg.settings.freeGiftsV2 || []),
        ...(gfg.settings.bogoV2 || [])
      ];
    }

    // Avoid duplicate campaign records.
    const uniqueCampaigns = new Map();

    campaigns.forEach(function (campaign) {
      if (!campaign || !campaign._id) {
        return;
      }

      if (campaign.isEnabled === false) {
        return;
      }

      uniqueCampaigns.set(campaign._id, campaign);
    });

    return Array.from(uniqueCampaigns.values());
  }

  /**
   * Identify a campaign from its KITA ID, campaign name,
   * campaign URL code, or an existing campaign slug.
   */
  function findCampaign(identifier, campaigns) {
    if (!identifier) {
      return null;
    }

    const value = String(identifier).trim();
    const normalizedValue = normalize(value);

    return campaigns.find(function (campaign) {
      const campaignId = String(campaign._id || '');
      const campaignTitle = normalize(campaign.title);

      const campaignUrlValue = String(
        campaign.urlBasedCampaign?.value || ''
      );

      return (
        campaignId === value ||
        campaignTitle === normalizedValue ||
        campaignUrlValue === value ||
        normalize(campaign.urlBasedCampaign?.value) ===
          normalizedValue
      );
    }) || null;
  }

  /**
   * Determine the current storefront language.
   */
  function getLocale() {
    const path = window.location.pathname.toLowerCase();

    if (/^\/en(?:\/|$)/.test(path)) {
      return 'en';
    }

    if (/^\/da(?:\/|$)/.test(path)) {
      return 'da';
    }

    if (/^\/sv(?:\/|$)/.test(path)) {
      return 'sv';
    }

    const language = (
      document.documentElement.lang ||
      window.Shopify?.locale ||
      'sv'
    ).toLowerCase();

    if (language.startsWith('en')) {
      return 'en';
    }

    if (language.startsWith('da')) {
      return 'da';
    }

    return 'sv';
  }

  /**
   * Get the first product from a KITA campaign's buy/get rules.
   */
  function getRuleProduct(campaign, ruleType) {
    const segments =
      campaign.segmentsData?.segmentsList || [];

    for (const segment of segments) {
      const rules =
        segment[ruleType]?.rulesList || [];

      for (const rule of rules) {
        const products = rule.ruleValue?.value || [];

        const product = products.find(function (item) {
          return item && (item.handle || item.title || item.name);
        });

        if (product) {
          return {
            handle: product.handle || '',
            title:
              product.title ||
              product.name ||
              product.handle ||
              ''
          };
        }
      }
    }

    return null;
  }

  /**
   * Remove HTML from KITA's subtitle before displaying it
   * as plain notification text.
   */
  function plainText(value) {
    const element = document.createElement('div');
    element.innerHTML = String(value || '');

    return (element.textContent || element.innerText || '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /**
   * Get translated campaign content from KITA.
   */
  function getCampaignDescription(campaign, locale) {
    const buyProduct = getRuleProduct(
      campaign,
      'buyXRulesData'
    );

    const freeProduct = getRuleProduct(
      campaign,
      'getYRulesData'
    );

    const configurations =
      campaign.widgetSettings?.configuration || {};

    const localizedConfiguration =
      configurations[locale] || configurations.en || {};

    const localizedTier =
      localizedConfiguration.tierConfig?.[0] || {};

    let subtitle =
      localizedTier.conditionNotMet?.subtitle || '';

    // Some KITA campaigns store their content in widgets instead.
    if (!subtitle) {
      const widgets = campaign.widgets || [];

      for (const widget of widgets) {
        const tier =
          widget.content?.[locale]?.tierSettings?.[0] ||
          widget.content?.en?.tierSettings?.[0];

        if (tier?.content?.[locale]?.subtitle) {
          subtitle = tier.content[locale].subtitle;
          break;
        }

        if (tier?.content?.en?.subtitle) {
          subtitle = tier.content.en.subtitle;
          break;
        }
      }
    }

    const hasGenericPlaceholder =
      /\{\{\s*PRODUCT_LINK\s*\}\}/i.test(subtitle);

    if (hasGenericPlaceholder) {
      subtitle = '';
    }

    if (subtitle) {
      subtitle = subtitle
        .replace(
          /\{\{\s*FREE_GIFT_PRODUCT\s*\}\}/gi,
          freeProduct?.title || ''
        )
        .replace(
          /\{\{\s*PRODUCT_LINK\s*\}\}/gi,
          buyProduct?.title || ''
        );

      subtitle = plainText(subtitle);
    }

    // Some campaigns have only generic English text.
    // Generate a readable fallback in the storefront language.
    if (!subtitle) {
      const buyName = buyProduct?.title || '';
      const giftName = freeProduct?.title || '';

      if (buyName && giftName) {
        if (locale === 'da') {
          subtitle = `Køb ${buyName} og få ${giftName} gratis.`;
        } else if (locale === 'en') {
          subtitle = `Buy ${buyName} and get ${giftName} free.`;
        } else {
          subtitle = `Köp ${buyName} och få ${giftName} gratis.`;
        }
      } else {
        subtitle = campaign.title || '';
      }
    }

    return {
      description: subtitle,
      buyProduct: buyProduct
    };
  }


  /**
   * Get the localized title configured in KITA.
   * Replace {{FREE_GIFT_PRODUCT}} with the localized gift product name.
   */
  function getCampaignTitle(campaign, locale) {
  const widgets = campaign.widgets || [];

  for (const widget of widgets) {

    if (widget.enabled === false) {
      continue;
    }

    const tiers = widget.tierSettings || [];

    for (const tier of tiers) {
      const content =
        tier.content?.[locale] ||
        tier.content?.en ||
        tier.content?.sv ||
        tier.content?.da;

      if (content?.title) {
        const freeProduct = getRuleProduct(
          campaign,
          'getYRulesData'
        );

        return plainText(
          content.title.replace(
            /\{\{\s*FREE_GIFT_PRODUCT\s*\}\}/gi,
            freeProduct?.title || ''
          )
        );
      }
    }
  }

  // Preserve the existing title if KITA has no localized title.
  return campaign.title || '';

  }

  /**
   * Build a localized product URL using Shopify's current
   * locale-aware storefront root.
   */
  function getProductUrl(handle) {
    if (!handle) {
      return '';
    }

    const root = window.Shopify?.routes?.root || '/';
    const baseUrl = new URL(root, window.location.origin);

    return new URL(
      'products/' + encodeURIComponent(handle),
      baseUrl
    ).pathname;
  }

  /**
   * Fetch the current Shopify cart.
   */
  async function getCart() {
    const root = window.Shopify?.routes?.root || '/';
    const cartUrl = new URL(
      'cart.js',
      new URL(root, window.location.origin)
    );

    const response = await fetch(cartUrl.toString(), {
      method: 'GET',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json'
      },
      cache: 'no-store'
    });

    if (!response.ok) {
      throw new Error(
        'Shopify cart request failed: ' + response.status
      );
    }

    return response.json();
  }

  /**
   * Determine whether the campaign's buy product is in the cart.
   *
   * Shopify cart items normally expose the product handle.
   * The product ID is used as a fallback when available.
   */
  function isCampaignProductInCart(cart, campaign) {
    if (!cart || !Array.isArray(cart.items) || !campaign) {
      return false;
    }

    const buyProduct = getRuleProduct(
      campaign,
      'buyXRulesData'
    );

    if (!buyProduct) {
      return false;
    }

    const targetHandle = normalize(buyProduct.handle);
    const targetTitle = normalize(buyProduct.title);

    return cart.items.some(function (item) {
      const itemHandle = normalize(item.handle);
      const itemTitle = normalize(item.product_title);

      if (targetHandle && itemHandle) {
        return targetHandle === itemHandle;
      }

      // Use title matching only if a usable handle is unavailable.
      return Boolean(
        !targetHandle &&
        targetTitle &&
        itemTitle &&
        targetTitle === itemTitle
      );
    });
  }

  /**
   * Minimize the popup and remember this state across navigation.
   */
  function minimizeNotification() {
    if (!activeCampaign || !activeCampaign._id) {
      return;
    }

    setMinimizedCampaignId(activeCampaign._id);

    hideNotification();
    showReopenButton();

    console.log(
      '[BOGO Notification] Campaign minimized:',
      activeCampaign.title,
      activeCampaign._id
    );
  }

  /**
   * Reopen the existing popup for the same campaign.
   */
  function reopenNotification() {
    if (!activeCampaign) {
      return;
    }

    const campaignId = String(activeCampaign._id);

    // Do not reopen if the campaign's buy product is already in the cart.
    checkCartAndUpdateNotification();

    clearMinimizedCampaignId();
    dismissedCampaignOnCurrentPage = null;

    hideReopenButton();
    showNotification(activeCampaign);

    console.log(
      '[BOGO Notification] Campaign reopened:',
      activeCampaign.title,
      campaignId
    );
  }

  /**
   * Attach minimize and reopen handlers only once.
   */
  function bindMinimizeAndReopenButtons() {
    const minimizeButton = document.querySelector(
      MINIMIZE_SELECTOR
    );

    if (
      minimizeButton &&
      !minimizeButton.dataset.bogoMinimizeBound
    ) {
      minimizeButton.dataset.bogoMinimizeBound = 'true';

      minimizeButton.addEventListener('click', function () {
        minimizeNotification();
      });
    }

    const reopenButton = document.querySelector(
      REOPEN_SELECTOR
    );

    if (
      reopenButton &&
      !reopenButton.dataset.bogoReopenBound
    ) {
      reopenButton.dataset.bogoReopenBound = 'true';

      reopenButton.addEventListener('click', function () {
        reopenNotification();
      });
    }
  }

  /**
   * Check the cart and update the notification.
   * The minimized state is preserved when the user navigates
   * between pages in the same browser tab.
   */
  async function checkCartAndUpdateNotification() {
    if (!activeCampaign || cartCheckInProgress) {
      return;
    }

    cartCheckInProgress = true;

    try {
      const campaignBeingChecked = activeCampaign;
      const campaignId = String(campaignBeingChecked._id);

      const cart = await getCart();

      // Another campaign may have become active while the
      // cart request was running.
      if (
        !activeCampaign ||
        String(activeCampaign._id) !== campaignId
      ) {
        return;
      }

      if (
        isCampaignProductInCart(
          cart,
          campaignBeingChecked
        )
      ) {
        hideNotification();
        hideReopenButton();

        console.log(
          '[BOGO Notification] Hidden because the campaign product is in the cart:',
          campaignBeingChecked.title
        );
      } else if (
        getMinimizedCampaignId() === campaignId
      ) {
        // Keep the popup minimized when changing pages.
        hideNotification();
        showReopenButton();
      } else {
        hideReopenButton();

        // Preserve the existing current-page dismissal behavior.
        if (
          dismissedCampaignOnCurrentPage !== campaignId
        ) {
          showNotification(campaignBeingChecked);
        }
      }

      lastCartCheck = Date.now();
    } catch (error) {
      console.warn(
        '[BOGO Notification] Could not check the cart.',
        error
      );
    } finally {
      cartCheckInProgress = false;
    }
  }

  /**
   * Display the existing notification without changing its design.
   */
  function showNotification(campaign) {
    const notification = document.querySelector(ROOT_SELECTOR);

    if (!notification || !campaign) {
      return;
    }

    const titleElement =
      notification.querySelector(TITLE_SELECTOR);

    const descriptionElement =
      notification.querySelector(DESCRIPTION_SELECTOR);

    const ctaElement =
      notification.querySelector(CTA_SELECTOR);

    // Retain compatibility with the original cross button if present.
    const closeElement =
      notification.querySelector(CLOSE_SELECTOR);

    const locale = getLocale();

    const content = getCampaignDescription(
      campaign,
      locale
    );

    if (titleElement) {
      titleElement.textContent = getCampaignTitle(
        campaign,
        locale
      );
    }

    if (descriptionElement) {
      descriptionElement.textContent = content.description;
    }

    if (ctaElement && content.buyProduct?.handle) {
      ctaElement.href = getProductUrl(
        content.buyProduct.handle
      );
    }

    notification.hidden = false;
    notification.style.display = 'block';
    notification.setAttribute('aria-hidden', 'false');

    hideReopenButton();
    bindMinimizeAndReopenButtons();

    // Preserve the original close behavior if the old button exists.
    if (closeElement && !closeElement.dataset.bogoBound) {
      closeElement.dataset.bogoBound = 'true';

      closeElement.addEventListener('click', function () {
        dismissedCampaignOnCurrentPage = String(campaign._id);

        notification.hidden = true;
        notification.style.display = 'none';
        notification.setAttribute('aria-hidden', 'true');
      });
    }

    console.log(
      '[BOGO Notification] Displaying campaign:',
      campaign.title,
      campaign._id
    );
  }

  /**
   * Hide the notification.
   */
  function hideNotification() {
    const notification = document.querySelector(ROOT_SELECTOR);

    if (notification) {
      notification.hidden = true;
      notification.style.display = 'none';
      notification.setAttribute('aria-hidden', 'true');
    }
  }

  /**
   * Intercept same-origin cart-changing requests.
   *
   * This detects common Shopify AJAX cart operations without
   * changing the request or its response.
   */
  function watchCartRequests() {
    if (window.__bogoCartWatcherInstalled) {
      return;
    }

    window.__bogoCartWatcherInstalled = true;

    const originalFetch = window.fetch;

    window.fetch = function () {
      const args = arguments;
      const request = args[0];

      let requestUrl = '';

      try {
        requestUrl =
          typeof request === 'string'
            ? request
            : request?.url || '';
      } catch (error) {
        requestUrl = '';
      }

      const result = originalFetch.apply(this, args);

      if (
        /\/cart\/(add|change|update|clear)(\.js)?(?:\?|$)/i.test(
          requestUrl
        )
      ) {
        result.then(function (response) {
          if (response.ok) {
            window.setTimeout(function () {
              checkCartAndUpdateNotification();
            }, 150);
          }
        }).catch(function () {
          // Leave normal fetch error handling untouched.
        });
      }

      return result;
    };

    // Also listen for native form-based cart additions.
    document.addEventListener('submit', function (event) {
      const form = event.target;

      if (!(form instanceof HTMLFormElement)) {
        return;
      }

      const action = form.getAttribute('action') || '';

      if (/\/cart\/add(?:\.js)?(?:\?|$)/i.test(action)) {
        window.setTimeout(function () {
          checkCartAndUpdateNotification();
        }, 1000);
      }
    }, true);

    // Support themes/apps that announce cart changes.
    [
      'cart:updated',
      'cart:refresh',
      'cart:change',
      'cart:build',
      'product:added',
      'ajaxProduct:added'
    ].forEach(function (eventName) {
      document.addEventListener(eventName, function () {
        checkCartAndUpdateNotification();
      });
    });
  }

  /**
   * Wait for KITA's storefront JavaScript to initialize.
   */
  function initialize() {
    const campaigns = getCampaigns();

    if (!campaigns.length) {
      return false;
    }

    const params = new URLSearchParams(
      window.location.search
    );

    // Our custom Meta Ad tracking parameter.
    const bogoIdentifier = params.get('bogo_offer');

    // Also recognize KITA campaign codes when present.
    const kiteIdentifier = params.get('kite_campaign');

    const explicitIdentifier =
      bogoIdentifier || kiteIdentifier;

    let campaign = null;

    if (explicitIdentifier) {
      campaign = findCampaign(
        explicitIdentifier,
        campaigns
      );

      if (campaign) {
        // An explicit campaign link should open the full popup.
        clearMinimizedCampaignId();

        sessionStorage.setItem(
          ACTIVE_KEY,
          campaign._id
        );

        // A new explicit campaign visit can reopen the notice.
        if (
          sessionStorage.getItem(CLOSED_KEY) !== campaign._id
        ) {
          sessionStorage.removeItem(CLOSED_KEY);
        }
      } else {
        // Do not accidentally display an old offer if an
        // explicitly supplied campaign identifier is invalid.
        sessionStorage.removeItem(ACTIVE_KEY);
        clearMinimizedCampaignId();

        activeCampaign = null;

        hideNotification();
        hideReopenButton();

        return true;
      }
    } else {
      // Preserve the campaign as the visitor browses the store.
      const activeIdentifier =
        sessionStorage.getItem(ACTIVE_KEY);

      campaign = findCampaign(
        activeIdentifier,
        campaigns
      );
    }

    if (!campaign) {
      activeCampaign = null;

      hideNotification();
      hideReopenButton();

      return true;
    }

    activeCampaign = campaign;

    // Bind the buttons on every page where the snippet exists.
    bindMinimizeAndReopenButtons();

    // Hide first to avoid briefly displaying the notice
    // before the cart check finishes.
    hideNotification();

    // The cart check decides whether to show the popup
    // or keep it minimized with the floating gift button.
    checkCartAndUpdateNotification();

    return true;
  }

  function start() {
    watchCartRequests();

    // Bind early in case the buttons already exist.
    bindMinimizeAndReopenButtons();

    let attempts = 0;

    const timer = window.setInterval(function () {
      attempts += 1;

      if (initialize() || attempts >= 40) {
        window.clearInterval(timer);
      }
    }, 250);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();